import { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { Layers, LocateFixed, Maximize2 } from "lucide-react";
import { MapFullScreenContext } from "./MapFullScreen";

/**
 * #1620 (D439) — the one compact control on the new-look maps: Locate, Map/Satellite and Full screen, a
 * vertical pill of 44px buttons in the map's top-right corner. It replaces Leaflet's +/− box, the
 * Street/Satellite panel and the separate full-screen button on the maps that mount it (the shipped maps
 * keep all three, D217). Pinch, double-tap, the scroll wheel and the keyboard's +/− still zoom.
 *
 * It is a real Leaflet control (`topright`) with a React portal into its container, so Leaflet keeps the
 * corner stacking and swallows the clicks that would otherwise pan the map. Each button is only drawn when
 * the host supplies what it needs: `onLocate`, a full-screen handler (`fullScreen`, or the `MapFullScreen` the
 * map sits in), and always the basemap toggle.
 *
 * `basemap` is `satellite` or `street`; the toggle's label is fixed ("Satellite") and `aria-pressed` says whether satellite is on (D459).
 */
// Order, top to bottom: full screen, map/satellite, locate. Full screen keeps the corner the old standalone button
// held (top-right of the map, journey 62 measures it there); locate sits last, under the thumb.
export const MAP_PILL_CLASS = "nw-map-pill";
const TAP = "var(--nw-size-tap, 44px)";

const buttonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: TAP,
  height: TAP,
  minWidth: TAP,
  minHeight: TAP,
  padding: 0,
  margin: 0,
  background: "var(--nw-surface)",
  color: "var(--nw-ink)",
  border: "none",
  borderRadius: 0,
  cursor: "pointer",
};

function PillButton({ label, testId, onClick, pressed, disabled, busy, buttonRef, children }) {
  return (
    <button
      ref={buttonRef}
      type="button"
      className="nw-map-pill-btn"
      data-testid={testId}
      aria-label={label}
      aria-pressed={pressed}
      aria-busy={busy || undefined}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      style={buttonStyle}
    >
      {children}
    </button>
  );
}

export default function MapControlPill({ basemap, onBasemap, onLocate, locating = false, fullScreen, fullScreenRef }) {
  const map = useMap();
  // Full screen: the host's own handler, else the `MapFullScreen` this map sits in. Inline only; while the
  // overlay is open its "Close map" strip is the way out and the button is not drawn.
  const fs = useContext(MapFullScreenContext);
  const openFullScreen = fullScreen || (fs && !fs.open ? fs.openMap : undefined);
  const openRef = fullScreen ? fullScreenRef : fs?.openBtnRef;
  const [host, setHost] = useState(null);

  useEffect(() => {
    const Pill = L.Control.extend({
      onAdd() {
        const el = L.DomUtil.create("div", MAP_PILL_CLASS);
        L.DomEvent.disableClickPropagation(el);
        // A tap that takes its own button out of the page (full screen opening) must not reach the map either:
        // leaflet tells a control's click from a map click by walking up from the target, which a detached
        // target no longer can. `disableClickPropagation` leaves 'click' itself to that walk.
        L.DomEvent.on(el, "click", L.DomEvent.stopPropagation);
        L.DomEvent.disableScrollPropagation(el);
        return el;
      },
    });
    const control = new Pill({ position: "topright" });
    control.addTo(map);
    setHost(control.getContainer());
    return () => {
      control.remove();
      setHost(null);
    };
  }, [map]);

  if (!host) return null;
  const satellite = basemap === "satellite";
  return createPortal(
    <>
      {openFullScreen && (
        <PillButton label="Show map full screen" testId="map-fullscreen-open" onClick={openFullScreen} buttonRef={openRef}>
          <Maximize2 size={20} aria-hidden="true" />
        </PillButton>
      )}
      <PillButton
        label="Satellite"
        testId="map-basemap-toggle"
        pressed={satellite}
        onClick={() => onBasemap(satellite ? "street" : "satellite")}
      >
        <Layers size={20} aria-hidden="true" />
      </PillButton>
      {onLocate && (
        <PillButton label="Show my location" testId="map-locate" onClick={onLocate} disabled={locating} busy={locating}>
          <LocateFixed size={20} aria-hidden="true" />
        </PillButton>
      )}
    </>,
    host,
  );
}
